import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  ButtonGroup,
  Button,
  Tab,
  Nav,
  Badge,
  Alert,
  Modal,
  Form,
  Spinner,
  Stack,
  Table
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import moment from 'moment';
import { fires } from '../firebase';
import { FaUsers, FaShoppingCart, FaChartLine, FaMoneyBill, FaGift, FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function AnalyticsDashboard() {
  const [timeRange, setTimeRange] = useState(7);
  const [customDateRange, setCustomDateRange] = useState(false);
  const [startDate, setStartDate] = useState(moment().startOf('day').subtract(7, 'days').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('day').toDate());
  const [tempStartDate, setTempStartDate] = useState(moment().startOf('day').subtract(7, 'days').toDate());
  const [tempEndDate, setTempEndDate] = useState(moment().endOf('day').toDate());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleOrdersClick = () => {
    navigate('/order-management');
  };

  const handleCustomersClick = () => {
    navigate('/user-management');
  };
  const [orderData, setOrderData] = useState({
    daily: [],
    total: 0,
    revenue: 0,
    totalTransactions: 0,
    growth: 0,
    revenueGrowth: 0,
    transactionGrowth: 0,
    freeOrders: 0,
    paidOrders: 0,
    dailyFreeOrders: [],
    dailyPaidOrders: [],
    ordersWithoutRates: 0
  });
  const [customerData, setCustomerData] = useState({
    daily: [],
    new: 0,
    growth: 0,
    active: 0,
    activeGrowth: 0
  });
  const [visitData, setVisitData] = useState({
    daily: [],
    total: 0,
    unique: 0,
    growth: 0,
    platforms: {
      mobile: 0,
      desktop: 0,
      tablet: 0
    },
    withUserAgent: 0,
    withoutUserAgent: 0,
    userAgents: {},
    domains: {
      'evtgo.com': 0,
      'eventgo.ca': 0,
      'other': 0
    },
    pageTypes: {
      'event': 0,
      'host': 0,
      'other': 0
    },
    urlBreakdown: []
  });
  const [eventData, setEventData] = useState({
    daily: [],
    total: 0,
    byType: {},
    growth: 0
  });
  const [eventOrders, setEventOrders] = useState({
    events: [],
    loading: false,
    error: null
  });
  const [organizationData, setOrganizationData] = useState({
    daily: [],
    total: 0,
    new: 0,
    growth: 0,
    byType: {},
    memberCount: 0,
    eventCount: 0,
    organizations: []
  });
  const [urlPage, setUrlPage] = useState(1);
  const [createdEventsData, setCreatedEventsData] = useState({
    events: [],
    loading: false,
    error: null
  });
  const [eventModalEvent, setEventModalEvent] = useState(null);
  const [eventModalData, setEventModalData] = useState(null);
  const [eventModalLoading, setEventModalLoading] = useState(false);

  const timeRanges = useMemo(() => [
    { days: 7, label: '1 Week' },
    { days: 30, label: '1 Month' },
    { days: 90, label: '3 Months' },
    { days: 180, label: '6 Months' },
    { days: 365, label: '1 Year' },
    { days: 'custom', label: 'Custom' }
  ], []);

  const handleTimeRangeChange = useCallback((days) => {
    if (days === 'custom') {
      // Set temp dates to current dates when opening picker
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setShowDatePicker(true);
      return;
    }
    // Don't trigger loading automatically - just update the dates
    setCustomDateRange(false);
    setTimeRange(days);
    setStartDate(moment().startOf('day').subtract(days, 'days').toDate());
    setEndDate(moment().endOf('day').toDate());
    setError(null);
  }, [startDate, endDate]);

  const handleApplyDateRange = useCallback(() => {
    try {
      if (!tempStartDate || !tempEndDate) {
        throw new Error('Please select both start and end dates');
      }
      if (moment(tempEndDate).isBefore(tempStartDate)) {
        throw new Error('End date must be after start date');
      }
      setShowDatePicker(false);
      setCustomDateRange(true);
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      const diffDays = moment(tempEndDate).diff(moment(tempStartDate), 'days');
      setTimeRange(diffDays);
      setError(null);
      // Trigger data loading
      setDataLoading(true);
    } catch (err) {
      setError(err.message || 'Error setting custom date range');
      console.error('Error in handleApplyDateRange:', err);
    }
  }, [tempStartDate, tempEndDate]);

  const handleTimeRangeButtonClick = useCallback((days) => {
    if (days === 'custom') {
      handleTimeRangeChange('custom');
      return;
    }
    // For non-custom ranges, trigger loading when button is clicked
    setCustomDateRange(false);
    setDataLoading(true);
    setTimeRange(days);
    setStartDate(moment().startOf('day').subtract(days, 'days').toDate());
    setEndDate(moment().endOf('day').toDate());
    setError(null);
  }, [handleTimeRangeChange]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          navigate('/');
          return;
        }
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        setIsAdmin(true);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Error verifying admin access');
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!isAdmin || loading) return;
    
    // Load on initial mount, or when dataLoading is explicitly set to true
    if (!initialLoad && !dataLoading) return;

    const fetchData = async () => {
      try {
        setDataLoading(true);
        setError(null);
        
        if (!startDate || !endDate) {
          throw new Error('Invalid date range');
        }

        const queryStartDate = moment(startDate).startOf('day').toDate();
        const queryEndDate = moment(endDate).endOf('day').toDate();
        const previousStartDate = moment(startDate)
          .subtract(moment(endDate).diff(moment(startDate), 'days'), 'days')
          .startOf('day')
          .toDate();

        // Batch size for pagination
        const BATCH_SIZE = 500;

        // Function to fetch data in batches with error handling
        const fetchInBatches = async (collection, startDate, endDate, processData) => {
          let lastDoc = null;
          let allData = [];
          let hasMore = true;

          while (hasMore) {
            try {
              let query = fires.collection(collection)
                .where('timestamp', '>=', startDate)
                .where('timestamp', '<=', endDate)
                .orderBy('timestamp', 'asc')
                .limit(BATCH_SIZE);

              if (lastDoc) {
                query = query.startAfter(lastDoc);
              }

              const snapshot = await query.get();
              
              if (snapshot.empty) {
                hasMore = false;
                break;
              }

              const batchData = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
              }));

              allData = [...allData, ...batchData];
              lastDoc = snapshot.docs[snapshot.docs.length - 1];
              
              if (snapshot.docs.length < BATCH_SIZE) {
                hasMore = false;
              }
            } catch (indexError) {
              // Fallback: if index error, try without orderBy
              if (indexError.code === 'failed-precondition') {
                console.warn(`Index error for ${collection}, trying without orderBy...`);
                try {
                  const allDocs = await fires.collection(collection).get();
                  const filtered = allDocs.docs
                    .map(doc => ({
                      ...doc.data(),
                      id: doc.id
                    }))
                    .filter(doc => {
                      const docTimestamp = doc.timestamp?.toDate?.() || doc.timestamp;
                      return docTimestamp >= startDate && docTimestamp <= endDate;
                    })
                    .sort((a, b) => {
                      const aTime = a.timestamp?.toDate?.() || a.timestamp || 0;
                      const bTime = b.timestamp?.toDate?.() || b.timestamp || 0;
                      return aTime - bTime;
                    });
                  
                  allData = filtered;
                  hasMore = false;
                } catch (fallbackError) {
                  console.error(`Error in fallback for ${collection}:`, fallbackError);
                  hasMore = false;
                }
              } else {
                throw indexError;
              }
            }
          }

          return await processData(allData);
        };

        // Process orders data
        const processOrders = (orders) => {
          const dailyOrders = {};
          const dailyFreeOrders = {};
          const dailyPaidOrders = {};
          let totalRevenue = 0;
          let totalTransactions = 0;
          let previousPeriodOrders = 0;
          let currentPeriodOrders = 0;
          let previousPeriodRevenue = 0;
          let currentPeriodRevenue = 0;
          let previousPeriodTransactions = 0;
          let currentPeriodTransactions = 0;
          let freeOrders = 0;
          let paidOrders = 0;
          let ordersWithoutRates = 0;
          const eventOrderMap = new Map();
          const eventRevenueMap = new Map();


          orders.forEach(order => {
            const orderDate = order.timestamp.toDate();
            const day = moment(orderDate).format('YYYY-MM-DD');
            // Use amount, totalAmount, or total; coerce to number so free (0) is excluded
            const rawAmount = order.amount || 0;
            const orderAmount = typeof rawAmount === 'number' ? rawAmount : parseFloat(rawAmount) || 0;

            const isInSelectedPeriod = orderDate >= startDate && orderDate <= endDate;
            const isPaidOrder = orderAmount > 0;

            // Calculate platform fee: use fees.rates if available, otherwise default to 3.5%
            let platformFee;
            let hasRates = false;

            if (order.fees?.rates !== undefined && order.fees?.rates !== null) {
              // fees.rates is a percentage (e.g., 3.5 for 3.5%)
              // Convert to decimal and calculate: orderAmount * (rates / 100)
              const rate = typeof order.fees.rates === 'number' ? order.fees.rates : parseFloat(order.fees.rates);
              platformFee = orderAmount * (rate / 100);
              hasRates = true;
            } else if (order.fees?.egFee !== undefined && order.fees?.egFee !== null) {
              // Fallback to egFee if rates is not available
              platformFee = order.fees.egFee;
              // Count only paid orders in selected period missing fees.rates (exclude free orders)
              if (isInSelectedPeriod && isPaidOrder) {
                ordersWithoutRates++;
              }
            } else {
              // Default to 3.5%
              platformFee = orderAmount * 0.035;
              // Count only paid orders in selected period missing fees.rates (exclude free orders)
              if (isInSelectedPeriod && isPaidOrder) {
                ordersWithoutRates++;
              }
            }

            const isFreeOrder = orderAmount === 0;
            
            if (!dailyOrders[day]) {
              dailyOrders[day] = { count: 0, revenue: 0, transactions: 0 };
              dailyFreeOrders[day] = { count: 0 };
              dailyPaidOrders[day] = { count: 0, revenue: 0, transactions: 0 };
            }
            
            dailyOrders[day].count++;
            dailyOrders[day].revenue += platformFee; // Use platform fee instead of full amount
            dailyOrders[day].transactions += orderAmount; // Track transaction amounts
            
            if (isFreeOrder) {
              dailyFreeOrders[day].count++;
              if (orderDate >= startDate) freeOrders++;
            } else {
              dailyPaidOrders[day].count++;
              dailyPaidOrders[day].revenue += platformFee; // Use platform fee for paid orders
              dailyPaidOrders[day].transactions += orderAmount; // Track transaction amounts for paid orders
              if (orderDate >= startDate) paidOrders++;
            }

            totalRevenue += platformFee; // Use platform fee for total revenue
            totalTransactions += orderAmount; // Track total transaction amount

            if (orderDate >= startDate) {
              currentPeriodOrders++;
              currentPeriodRevenue += platformFee; // Use platform fee for current period
              currentPeriodTransactions += orderAmount; // Track current period transactions
            } else {
              previousPeriodOrders++;
              previousPeriodRevenue += platformFee; // Use platform fee for previous period
              previousPeriodTransactions += orderAmount; // Track previous period transactions
            }

            // Track event orders and revenue (ONLY within selected date range)
            if (order.eventId && orderDate >= startDate && orderDate <= endDate) {
              const count = eventOrderMap.get(order.eventId) || 0;
              const revenue = eventRevenueMap.get(order.eventId) || 0;
              const transactions = eventRevenueMap.get(`${order.eventId}_transactions`) || 0;
              const existingLastOrderDate = eventOrderMap.get(`${order.eventId}_lastOrder`);
              
              // Update last order date if this order is more recent within range
              if (!existingLastOrderDate || orderDate > existingLastOrderDate) {
                eventOrderMap.set(`${order.eventId}_lastOrder`, orderDate);
              }
              
              eventOrderMap.set(order.eventId, count + 1);
              eventRevenueMap.set(order.eventId, revenue + platformFee); // Use platform fee for event revenue
              eventRevenueMap.set(`${order.eventId}_transactions`, transactions + orderAmount); // Track transaction amounts
            }
          });

          return {
            daily: Object.entries(dailyOrders)
              .filter(([date]) => moment(date).toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                revenue: data.revenue,
                transactions: data.transactions
              })),
            total: currentPeriodOrders,
            revenue: currentPeriodRevenue,
            totalTransactions: currentPeriodTransactions,
            growth: previousPeriodOrders === 0 ? 100 : 
              ((currentPeriodOrders - previousPeriodOrders) / previousPeriodOrders) * 100,
            revenueGrowth: previousPeriodRevenue === 0 ? 100 :
              ((currentPeriodRevenue - previousPeriodRevenue) / previousPeriodRevenue) * 100,
            transactionGrowth: previousPeriodTransactions === 0 ? 100 :
              ((currentPeriodTransactions - previousPeriodTransactions) / previousPeriodTransactions) * 100,
            freeOrders,
            paidOrders,
            ordersWithoutRates,
            dailyFreeOrders: Object.entries(dailyFreeOrders)
              .filter(([date]) => moment(date).toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count
              })),
            dailyPaidOrders: Object.entries(dailyPaidOrders)
              .filter(([date]) => moment(date).toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                revenue: data.revenue,
                transactions: data.transactions
              })),
            eventOrderMap,
            eventRevenueMap,
            allOrders: orders // Include raw orders data for active customer calculation
          };
        };

        // Fetch new customers from users collection by created/createdAt in date range
        const fetchNewCustomers = async (rangeStart, rangeEnd) => {
          const runQuery = async (createdField) => {
            let allData = [];
            let lastDoc = null;
            let hasMore = true;
            const BATCH_SIZE = 500;
            while (hasMore) {
              try {
                let query = fires.collection('users')
                  .where(createdField, '>=', rangeStart)
                  .where(createdField, '<=', rangeEnd)
                  .orderBy(createdField, 'asc');
                if (lastDoc) query = query.startAfter(lastDoc);
                const snapshot = await query.get();
                if (snapshot.empty) {
                  hasMore = false;
                  break;
                }
                const batchData = snapshot.docs.map(doc => ({
                  ...doc.data(),
                  id: doc.id
                }));
                allData = [...allData, ...batchData];
                lastDoc = snapshot.docs[snapshot.docs.length - 1];
                if (snapshot.docs.length < BATCH_SIZE) hasMore = false;
              } catch (err) {
                if (err.code === 'failed-precondition') {
                  return null; // try next field or fallback
                }
                throw err;
              }
            }
            return allData;
          };
          let allData = await runQuery('created');
          if (!allData || allData.length === 0) {
            const withCreatedAt = await runQuery('createdAt');
            if (withCreatedAt && withCreatedAt.length > 0) allData = withCreatedAt;
          }
          if (!allData || allData.length === 0) {
            const withTimestamp = await runQuery('timestamp');
            if (withTimestamp && withTimestamp.length > 0) allData = withTimestamp;
          }
          if (!allData || allData.length === 0) {
            try {
              const allDocs = await fires.collection('users').get();
              allData = allDocs.docs
                .map(doc => ({ ...doc.data(), id: doc.id }))
                .filter(doc => {
                  const t = doc.created?.toDate?.() ?? doc.createdAt?.toDate?.() ?? doc.created ?? doc.createdAt ?? doc.timestamp?.toDate?.() ?? doc.timestamp;
                  const d = t instanceof Date ? t : (t && typeof t.toDate === 'function' ? t.toDate() : new Date(0));
                  return !isNaN(d.getTime()) && d >= rangeStart && d <= rangeEnd;
                });
            } catch (e) {
              console.warn('fetchNewCustomers fallback failed', e);
              allData = allData || [];
            }
          }
          return allData || [];
        };

        // Process new customers (users with created in range); output includes UIDs for current and previous period
        const processNewCustomers = (newCustomersList) => {
          const dailyNew = {};
          const getCreatedDate = (u) => {
            const t = u.created?.toDate?.() ?? u.createdAt?.toDate?.() ?? u.created ?? u.createdAt ?? u.timestamp?.toDate?.() ?? u.timestamp;
            return t instanceof Date ? t : (t && typeof t.toDate === 'function' ? t.toDate() : new Date(0));
          };
          let currentPeriodNew = 0;
          let previousPeriodNew = 0;
          const currentPeriodNewUIDs = new Set();
          const previousPeriodNewUIDs = new Set();

          newCustomersList.forEach(customer => {
            const createdDate = getCreatedDate(customer);
            const day = moment(createdDate).format('YYYY-MM-DD');
            if (createdDate >= startDate) {
              currentPeriodNew++;
              currentPeriodNewUIDs.add(customer.id);
              if (!dailyNew[day]) dailyNew[day] = 0;
              dailyNew[day]++;
            } else if (createdDate >= previousStartDate) {
              previousPeriodNew++;
              previousPeriodNewUIDs.add(customer.id);
            }
          });

          const daily = Object.entries(dailyNew)
            .filter(([date]) => moment(date).toDate() >= startDate)
            .map(([date, count]) => ({ date, count }));

          return {
            new: currentPeriodNew,
            daily,
            currentPeriodNewUIDs,
            previousPeriodNewUIDs,
            previousPeriodNew,
            growth: previousPeriodNew === 0 ? (currentPeriodNew > 0 ? 100 : 0) :
              ((currentPeriodNew - previousPeriodNew) / previousPeriodNew) * 100
          };
        };

        // Active users = unique UIDs from page_views in period, excluding new users (created in period). Does not include new users.
        const calculateActiveCustomers = (pageViews, currentPeriodNewUIDs, previousPeriodNewUIDs) => {
          const currentPeriodUIDs = new Set();
          const previousPeriodUIDs = new Set();
          const dailyActiveByDay = {};
          const endDateEnd = moment(endDate).endOf('day').toDate();

          pageViews.forEach(pageView => {
            const uid = pageView.uid ?? pageView.userId ?? pageView.user_id
              ?? pageView.metadata?.uid ?? pageView.metadata?.userId ?? pageView.metadata?.user_id
              ?? null;
            if (!uid || uid === "unknown") return;
            const rawTs = pageView.timestamp;
            const pageViewDate = rawTs?.toDate ? rawTs.toDate() : (rawTs ? new Date(rawTs) : null);
            if (!pageViewDate || isNaN(pageViewDate.getTime())) return;
            const day = moment(pageViewDate).startOf('day').format('YYYY-MM-DD');
            if (pageViewDate >= startDate && pageViewDate <= endDateEnd) {
              currentPeriodUIDs.add(uid);
              if (!dailyActiveByDay[day]) dailyActiveByDay[day] = new Set();
              dailyActiveByDay[day].add(uid);
            } else if (pageViewDate >= previousStartDate && pageViewDate < startDate) {
              previousPeriodUIDs.add(uid);
            }
          });

          // Exclude new users: active users must not include anyone created in the period
          currentPeriodNewUIDs.forEach(uid => currentPeriodUIDs.delete(uid));
          previousPeriodNewUIDs.forEach(uid => previousPeriodUIDs.delete(uid));
          Object.keys(dailyActiveByDay).forEach(day => {
            currentPeriodNewUIDs.forEach(uid => dailyActiveByDay[day].delete(uid));
          });

          const currentActive = currentPeriodUIDs.size;
          const previousActive = previousPeriodUIDs.size;
          const growth = previousActive === 0 ? (currentActive > 0 ? 100 : 0) :
            ((currentActive - previousActive) / previousActive) * 100;

          const dailyActive = Object.entries(dailyActiveByDay)
            .filter(([date]) => moment(date).startOf('day').toDate() >= startDate)
            .map(([date, set]) => ({ date, count: set.size }));

          return {
            active: currentActive,
            growth,
            dailyActive
          };
        };

        // Batch fetch user details using Firestore getAll (more efficient)
        const batchGetUserDetails = async (uids) => {
          const validUIDs = uids.filter(uid => uid && uid !== "unknown");
          if (validUIDs.length === 0) return new Map();
          
          const userDetailsMap = new Map();
          const BATCH_SIZE = 10; // Firestore getAll limit is 10
          
          for (let i = 0; i < validUIDs.length; i += BATCH_SIZE) {
            const batchUIDs = validUIDs.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchUIDs.map(uid => fires.collection('users').doc(uid));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const userData = snapshot.data();
                  userDetailsMap.set(batchUIDs[index], {
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    email: userData.email || '',
                    displayName: userData.displayName || ''
                  });
                }
              });
            } catch (error) {
              console.log('Error batch fetching user details:', error);
            }
          }
          
          return userDetailsMap;
        };

        // Batch fetch event approved status (orders from events with approved = false are not counted)
        const batchGetEventApproved = async (eventIds) => {
          const validEventIds = [...new Set(eventIds.filter(id => id && id !== "unknown"))];
          if (validEventIds.length === 0) return new Map();
          const approvedMap = new Map();
          const BATCH_SIZE = 10;
          for (let i = 0; i < validEventIds.length; i += BATCH_SIZE) {
            const batchIds = validEventIds.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchIds.map(id => fires.collection('events').doc(id));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const approved = snapshot.data().approved;
                  approvedMap.set(batchIds[index], approved === true);
                }
              });
            } catch (error) {
              console.log('Error batch fetching event approved:', error);
            }
          }
          return approvedMap;
        };

        // Batch fetch event details using Firestore getAll (more efficient)
        const batchGetEventDetails = async (eventIds) => {
          const validEventIds = eventIds.filter(eventId => eventId && eventId !== "unknown");
          if (validEventIds.length === 0) return new Map();
          
          const eventDetailsMap = new Map();
          const BATCH_SIZE = 10; // Firestore getAll limit is 10
          
          for (let i = 0; i < validEventIds.length; i += BATCH_SIZE) {
            const batchIds = validEventIds.slice(i, i + BATCH_SIZE);
            try {
              const docRefs = batchIds.map(eventId => fires.collection('events').doc(eventId));
              const snapshots = await Promise.all(docRefs.map(ref => ref.get()));
              
              snapshots.forEach((snapshot, index) => {
                if (snapshot.exists) {
                  const eventData = snapshot.data();
                  eventDetailsMap.set(batchIds[index], {
                    title: eventData.title || eventData.eventDetails?.title || 'Unknown Event',
                    id: batchIds[index]
                  });
                }
              });
            } catch (error) {
              console.log('Error batch fetching event details:', error);
            }
          }
          
          return eventDetailsMap;
        };

        // Process visits data
        const processVisits = async (visits) => {
          const dailyVisits = {};
          const uniqueIPs = new Set();
          const platforms = {
            mobile: 0,
            desktop: 0,
            tablet: 0
          };
          const userAgentMap = new Map();
          const domainMap = {
            'evtgo.com': 0,
            'eventgo.ca': 0,
            'other': 0
          };
          const pageTypeMap = {
            'event': 0,
            'host': 0,
            'hosts': 0,
            'category': 0,
            'eventgo-ai': 0,
            'create-new-event': 0,
            'order': 0,
            'about': 0,
            'other': 0
          };
          const urlBreakdown = [];
          let withUserAgent = 0;
          let withoutUserAgent = 0;
          let previousPeriodVisits = 0;
          let currentPeriodVisits = 0;
          
          visits.forEach(visit => {
            const day = moment(visit.timestamp.toDate()).format('YYYY-MM-DD');
            const visitDate = visit.timestamp.toDate();
            
            // Determine current vs previous period
            if (visitDate >= startDate && visitDate <= endDate) {
              // Current period
              currentPeriodVisits++;

              if (!dailyVisits[day]) {
                dailyVisits[day] = { count: 0 };
              }
              dailyVisits[day].count++;

              if (visit.currentIP) {
                uniqueIPs.add(visit.currentIP);
              }

              if (visit.platform === 'mobile') platforms.mobile++;
              else if (visit.platform === 'tablet') platforms.tablet++;
              else platforms.desktop++;

              // User agent analysis (current period only)
              if (visit.fromUA && visit.fromUA.trim() !== '') {
                withUserAgent++;
                
                // Parse user agent to get browser/device info
                const ua = visit.fromUA.toLowerCase();
                let userAgentType = 'Unknown';
                
                if (ua.includes('safari') && !ua.includes('chrome')) {
                  userAgentType = 'Safari';
                } else if (ua.includes('chrome')) {
                  userAgentType = 'Chrome';
                } else if (ua.includes('firefox')) {
                  userAgentType = 'Firefox';
                } else if (ua.includes('edge')) {
                  userAgentType = 'Edge';
                } else if (ua.includes('micromessenger')) {
                  userAgentType = 'WeChat';
                } else if (ua.includes('instagram')) {
                  userAgentType = 'Instagram';
                } else if (ua.includes('fb')) {
                  userAgentType = 'Facebook';
                } else if (ua.includes('linkedin')) {
                  userAgentType = 'LinkedIn';
                } else if (ua.includes('mobile')) {
                  userAgentType = 'Mobile Browser';
                } else {
                  userAgentType = 'Other';
                }
                
                userAgentMap.set(userAgentType, (userAgentMap.get(userAgentType) || 0) + 1);
              } else {
                withoutUserAgent++;
              }

              // URL analysis (current period only)
              if (visit.url) {
                const url = visit.url.toLowerCase()
                
                // Domain analysis
                if (url.includes('evtgo.com')) {
                  domainMap['evtgo.com']++;
                } else if (url.includes('eventgo.ca')) {
                  domainMap['eventgo.ca']++;
                } else {
                  domainMap['other']++;
                }
                
                // Page type analysis
                if (url.includes('/event/')) {
                  pageTypeMap['event']++;
                } 
                else if (url.includes('/host/')) {
                  pageTypeMap['host']++;
                }
                else if (url.includes('/hosts/')) {
                  pageTypeMap['hosts']++;
                }
                else if (url.includes('/category/')) {
                  pageTypeMap['category']++;
                }
                else if (url.includes('/eventgo-ai/')) {
                  pageTypeMap['eventgo-ai']++;
                }
                else if (url.includes('/create-new-event/')) {
                  pageTypeMap['create-new-event']++;
                }
                else if (url.includes('/order/')) {
                  pageTypeMap['order']++;
                }
                else if (url.includes('/about/')) {
                  pageTypeMap['about']++;
                }
                else {  
                  pageTypeMap['other']++;
                }
                
                               // URL breakdown for detailed analysis
                 const urlInfo = {
                   path: visit.url,
                   domain: url.includes('evtgo.com') ? 'evtgo.com' : 
                          url.includes('eventgo.ca') ? 'eventgo.ca' : 'other',
                   pageType: url.includes('/event/') ? 'event page' : 
                            url.includes('/host/') ? 'host page' :
                            url.includes('/hosts/') ? 'hosts page' :
                            url.includes('/category/') ? 'search event page' :
                            url.includes('/eventgo-ai/') ? 'ask-ai page' :
                            url.includes('/create-new-event/') ? 'create event page' :
                            url.includes('/order/') ? 'order page' :
                            url.includes('/about/') ? 'about page' : 'other',
                   eventId: url.includes('/event/') ? url.split('/event/')[1]?.split('/')[0] || 'unknown' : null,
                   timestamp: visit.timestamp,
                   platform:  "unknown",
                   fromUA: visit.fromUA,
                   uid: visit.uid || "unknown",
                   userDetails: null // Will be populated later
                 };
                urlBreakdown.push(urlInfo);
              }
            } else {
              // Previous period
              previousPeriodVisits++;
            }
          });

          // Fetch user details for visits with UIDs using optimized batch method
          const uniqueUIDs = [...new Set(visits.map(visit => visit.uid).filter(uid => uid && uid !== "unknown"))];
          const userDetailsMap = await batchGetUserDetails(uniqueUIDs);

          // Fetch event details for visits with event IDs using optimized batch method
          const uniqueEventIds = [...new Set(urlBreakdown.map(url => url.eventId).filter(eventId => eventId && eventId !== "unknown"))];
          const eventDetailsMap = await batchGetEventDetails(uniqueEventIds);

          // Attach user details to URL breakdown
          urlBreakdown.forEach(urlInfo => {
            if (urlInfo.uid && urlInfo.uid !== "unknown") {
              urlInfo.userDetails = userDetailsMap.get(urlInfo.uid) || null;
            }
            if (urlInfo.eventId && urlInfo.eventId !== "unknown") {
              urlInfo.eventDetails = eventDetailsMap.get(urlInfo.eventId) || null;
            }
          });

          return {
            daily: Object.entries(dailyVisits).map(([date, data]) => ({
              date,
              count: data.count
            })),
            total: currentPeriodVisits,
            unique: uniqueIPs.size,
            growth: previousPeriodVisits === 0 ? 100 :
              ((currentPeriodVisits - previousPeriodVisits) / previousPeriodVisits) * 100,
            platforms,
            withUserAgent,
            withoutUserAgent,
            userAgents: Object.fromEntries(userAgentMap),
            domains: domainMap,
            pageTypes: pageTypeMap,
            urlBreakdown: urlBreakdown.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate()),
            allVisits: visits // Include raw visits data for active customer calculation
          };
        };

        // Process events data: Events Overview only shows events with status = live and approved = true
        const processEvents = (events) => {
          const liveApproved = (events || []).filter(
            (e) => e.status === 'live' && e.approved === true
          );
          const dailyEvents = {};
          const eventTypes = {};
          let previousPeriodEvents = 0;
          let currentPeriodEvents = 0;

          liveApproved.forEach(event => {
            const eventDate = event.timestamp.toDate();
            const day = moment(eventDate).format('YYYY-MM-DD');
            const eventType = event.type || 'unknown';
            
            if (!dailyEvents[day]) {
              dailyEvents[day] = { count: 0, types: {} };
            }
            
            dailyEvents[day].count++;
            dailyEvents[day].types[eventType] = (dailyEvents[day].types[eventType] || 0) + 1;
            eventTypes[eventType] = (eventTypes[eventType] || 0) + 1;

            if (eventDate <= endDate) {
              currentPeriodEvents++;
            } else {
              previousPeriodEvents++;
            }
          });

          return {
            daily: Object.entries(dailyEvents)
              .filter(([date]) => moment(date).toDate() <= endDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                ...data.types
              })),
            total: currentPeriodEvents,
            byType: eventTypes,
            growth: previousPeriodEvents === 0 ? 100 :
              ((currentPeriodEvents - previousPeriodEvents) / previousPeriodEvents) * 100
          };
        };

        // Process organizations data
        const processOrganizations = (organizations) => {
          const dailyOrganizations = {};
          const organizationTypes = {};
          let previousPeriodOrganizations = 0;
          let currentPeriodOrganizations = 0;
          let totalMembers = 0;
          let totalEvents = 0;
          const organizationsInPeriod = [];

          organizations.forEach(org => {
            const orgDate = org.timestamp.toDate();
            const day = moment(orgDate).format('YYYY-MM-DD');
            const orgType = org.type || 'unknown';
            
            if (!dailyOrganizations[day]) {
              dailyOrganizations[day] = { count: 0, types: {} };
            }
            
            dailyOrganizations[day].count++;
            dailyOrganizations[day].types[orgType] = (dailyOrganizations[day].types[orgType] || 0) + 1;
            organizationTypes[orgType] = (organizationTypes[orgType] || 0) + 1;

            // Count members and events
            const memberCount = org.member_count || 0;
            const eventCount = org.event_count || 0;
            totalMembers += memberCount;
            totalEvents += eventCount;

            if (orgDate >= startDate) {
              currentPeriodOrganizations++;
              // Add to organizations list for display
              organizationsInPeriod.push({
                id: org.id,
                name: org.name || org.groupName || 'Untitled Organization',
                type: orgType,
                profileURL: org.profileURL || org.profilePicture || org.avatar,
                memberCount: memberCount,
                eventCount: eventCount,
                timestamp: org.timestamp
              });
            } else {
              previousPeriodOrganizations++;
            }
          });

          // Sort organizations by creation date (newest first)
          organizationsInPeriod.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());

          return {
            daily: Object.entries(dailyOrganizations)
              .filter(([date]) => moment(date).toDate() >= startDate)
              .map(([date, data]) => ({
                date,
                count: data.count,
                ...data.types
              })),
            total: currentPeriodOrganizations,
            new: Object.values(dailyOrganizations).reduce((acc, curr) => acc + curr.count, 0),
            growth: previousPeriodOrganizations === 0 ? 100 :
              ((currentPeriodOrganizations - previousPeriodOrganizations) / previousPeriodOrganizations) * 100,
            byType: organizationTypes,
            memberCount: totalMembers,
            eventCount: totalEvents,
            organizations: organizationsInPeriod
          };
        };



        // Fetch all data in parallel (raw orders so we can filter by event approved)
        const [rawOrders, newCustomersRaw, visitsData, eventsData, organizationsData] = await Promise.all([
          fetchInBatches('orders', previousStartDate, queryEndDate, (orders) => orders),
          fetchNewCustomers(previousStartDate, queryEndDate),
          fetchInBatches('page_views', previousStartDate, queryEndDate, processVisits),
          fetchInBatches('events', previousStartDate, queryEndDate, processEvents),
          fetchInBatches('groups', previousStartDate, queryEndDate, processOrganizations)
        ]);

        // Orders from events with approved = false are not counted; orders without eventId or with approved !== false are counted
        const orderEventIds = [...new Set((rawOrders || []).map(o => o.eventId).filter(Boolean))];
        const eventApprovedMap = await batchGetEventApproved(orderEventIds);
        const ordersForCount = (rawOrders || []).filter(order => {
          if (!order.eventId) return true;
          const approved = eventApprovedMap.get(order.eventId);
          return approved !== false;
        });
        const ordersData = processOrders(ordersForCount);

        const customersData = processNewCustomers(newCustomersRaw);

        // Process created events data (optimized with batch user fetching)
        const processCreatedEvents = async (events) => {
          const createdEventsInPeriod = [];
          const eventUIDs = new Set();
          
          // First pass: collect all events in period and UIDs
          for (const event of events) {
            const eventDate = event.timestamp.toDate();
            
            if (eventDate >= startDate && eventDate <= endDate) {
              const eventUID = event.fromUID || event.user?.uid;
              if (eventUID) {
                eventUIDs.add(eventUID);
              }
              
              createdEventsInPeriod.push({
                id: event.id,
                title: event.title || event.eventDetails?.title || 'Untitled Event',
                startDate: event.startDate ? event.startDate.toDate() : null,
                endDate: event.endDate ? event.endDate.toDate() : null,
                location: event.address?.fullAddress || event.location || event.eventDetails?.location || 'No location specified',
                hostInfo: null, // Will be populated below
                organizationInfo: event.user && event.user.userName ? {
                  userName: event.user.userName,
                  organizationName: event.user.organizationName || event.user.groupName || event.user.userName,
                  organizationId: event.user.organizationId || event.user.groupId || null
                } : null,
                timestamp: event.timestamp,
                status: event.status || 'unknown',
                type: event.type || 'unknown',
                eventUID: eventUID // Store for later matching
              });
            }
          }
          
          // Batch fetch all user details at once
          const userDetailsMap = await batchGetUserDetails([...eventUIDs]);
          
          // Second pass: populate host info from batch results
          createdEventsInPeriod.forEach(event => {
            if (event.eventUID && userDetailsMap.has(event.eventUID)) {
              const userData = userDetailsMap.get(event.eventUID);
              event.hostInfo = {
                uid: event.eventUID,
                userName: userData.displayName || 
                         `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 
                         userData.email || 'Unknown User'
              };
            } else if (event.eventUID) {
              event.hostInfo = {
                uid: event.eventUID,
                userName: 'Unknown User'
              };
            }
            // Remove temporary eventUID property
            delete event.eventUID;
          });
          
          // Sort by creation date (newest first)
          createdEventsInPeriod.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());
          
          return createdEventsInPeriod;
        };

        // Fetch created events data
        const createdEvents = await fetchInBatches('events', queryStartDate, queryEndDate, processCreatedEvents);
        
        setCreatedEventsData({
          events: createdEvents,
          loading: false,
          error: null
        });

        // Process event orders after getting both orders and events data
        const eventOrderMap = ordersData.eventOrderMap;
        const eventRevenueMap = ordersData.eventRevenueMap;
        const eventIds = Array.from(eventOrderMap.keys()).filter(key => !key.includes('_lastOrder')); // Filter out date entries
        
        // Use optimized batch fetch function
        const eventDetailsMap = eventIds.length > 0 ? await batchGetEventDetails(eventIds) : new Map();
        
        // Map event details to include additional fields needed
        const enhancedEventDetailsMap = new Map();
        eventIds.forEach(eventId => {
          const baseDetails = eventDetailsMap.get(eventId);
          if (baseDetails) {
            enhancedEventDetailsMap.set(eventId, {
              title: baseDetails.title || 'Untitled Event',
              timestamp: null, // Not needed from batch fetch
              status: 'unknown' // Not needed from batch fetch
            });
          }
        });

        // Combine order counts with event details
        const eventOrdersList = Array.from(eventOrderMap.entries())
          .filter(([key]) => !key.includes('_lastOrder')) // Filter out the date entries
          .map(([eventId, orderCount]) => {
            const details = enhancedEventDetailsMap.get(eventId);
            return {
              eventId,
              orderCount,
              revenue: eventRevenueMap.get(eventId) || 0,
              transactions: eventRevenueMap.get(`${eventId}_transactions`) || 0,
              lastOrderDate: eventOrderMap.get(`${eventId}_lastOrder`),
              ...(details || {}),
            };
          })
          .filter(event => event.title) // Only include events with valid details
          .sort((a, b) => {
            // First sort by order count
            if (b.orderCount !== a.orderCount) {
              return b.orderCount - a.orderCount;
            }
            // If order counts are equal, sort by revenue
            return b.revenue - a.revenue;
          });

        setEventOrders({
          events: eventOrdersList,
          loading: false,
          error: null
        });

        // Active users = timestamp in given time from page_views, excluding new customers
        const activeCustomerData = calculateActiveCustomers(
          visitsData.allVisits || [],
          customersData.currentPeriodNewUIDs || new Set(),
          customersData.previousPeriodNewUIDs || new Set()
        );

        // Merge daily new and daily active into one chart dataset (all dates in range)
        const dailyNewMap = {};
        (customersData.daily || []).forEach(({ date, count }) => { dailyNewMap[date] = count; });
        const dailyActiveMap = {};
        (activeCustomerData.dailyActive || []).forEach(({ date, count }) => { dailyActiveMap[date] = count; });
        const allDays = new Set([
          ...Object.keys(dailyNewMap),
          ...Object.keys(dailyActiveMap),
          ...(() => {
            const d = [];
            let cur = moment(startDate).startOf('day');
            const end = moment(endDate).startOf('day');
            while (cur.isSameOrBefore(end)) {
              d.push(cur.format('YYYY-MM-DD'));
              cur.add(1, 'day');
            }
            return d;
          })()
        ]);
        // Build one row per day: new users and active users are calculated by day
        const mergedDaily = Array.from(allDays)
          .filter(date => moment(date).startOf('day').toDate() >= moment(startDate).startOf('day').toDate())
          .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
          .map(date => ({
            date,
            newUsers: Number(dailyNewMap[date]) || 0,
            activeUsers: Number(dailyActiveMap[date]) || 0
          }));

        // New = sum of daily new; Active = unique active users in period (not sum of daily)
        const newTotalByDay = mergedDaily.reduce((sum, row) => sum + (Number(row.newUsers) || 0), 0);

        setOrderData(ordersData);
        setCustomerData({
          new: newTotalByDay,
          daily: mergedDaily,
          growth: customersData.growth,
          active: activeCustomerData.active,
          activeGrowth: activeCustomerData.growth
        });
        setVisitData(visitsData);
        setEventData(eventsData);
        setOrganizationData(prevData => ({
          ...organizationsData,
          organizations: organizationsData.organizations || prevData.organizations || []
        }));

      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('Error loading analytics data. Please try again.');
        setEventOrders(prev => ({ ...prev, loading: false, error: err.message }));
      } finally {
        setDataLoading(false);
      }
    };

    if (initialLoad || dataLoading) {
      fetchData();
      if (initialLoad) {
        setInitialLoad(false);
      }
    }
  }, [dataLoading, isAdmin, loading, initialLoad, startDate, endDate]); // Include dates in dependency but only fetch when dataLoading is true or initial load

  // Fetch event summary for modal when user clicks an event
  useEffect(() => {
    if (!eventModalEvent?.id) {
      setEventModalData(null);
      return;
    }
    const id = eventModalEvent.id;
    setEventModalLoading(true);
    setEventModalData(null);
    (async () => {
      try {
        const [ordersSnap, viewsSnap] = await Promise.all([
          fires.collection('orders').where('eventId', '==', id).orderBy('timestamp', 'asc').get(),
          fires.collection('events').doc(id).collection('views').orderBy('timestamp', 'asc').get()
        ]);
        const orders = ordersSnap.docs.map(doc => {
          const d = doc.data();
          return { ...d, id: doc.id, createdAt: d.timestamp?.toDate?.() || new Date() };
        });
        const views = viewsSnap.docs.map(doc => {
          const d = doc.data();
          return { ...d, id: doc.id, viewedAt: d.timestamp?.toDate?.() || new Date() };
        });
        const userUIDs = new Set();
        orders.forEach(o => {
          const uid = o.userUID || o.userId || o.uid;
          if (uid) userUIDs.add(uid);
        });
        const orderTotal = orders.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);
        const byDay = new Map();
        const toYMD = (d) => (d instanceof Date ? d : new Date(d)).toISOString().slice(0, 10);
        orders.forEach(o => {
          const key = toYMD(o.createdAt);
          const prev = byDay.get(key) || { date: key, orders: 0, views: 0, userUIDs: new Set() };
          prev.orders += 1;
          const uid = o.userUID || o.userId || o.uid;
          if (uid) prev.userUIDs.add(uid);
          byDay.set(key, prev);
        });
        views.forEach(v => {
          const key = toYMD(v.viewedAt);
          const prev = byDay.get(key) || { date: key, orders: 0, views: 0, userUIDs: new Set() };
          prev.views += 1;
          byDay.set(key, prev);
        });
        const daily = Array.from(byDay.values())
          .map(row => ({ date: row.date, orders: row.orders, views: row.views, users: row.userUIDs ? row.userUIDs.size : 0 }))
          .sort((a, b) => a.date.localeCompare(b.date));
        setEventModalData({
          userCount: userUIDs.size,
          orderCount: orders.length,
          orderTotal,
          viewsCount: views.length,
          daily
        });
      } catch (err) {
        console.error('Error fetching event summary:', err);
        setEventModalData({ userCount: 0, orderCount: 0, orderTotal: 0, viewsCount: 0, daily: [] });
      }
      setEventModalLoading(false);
    })();
  }, [eventModalEvent?.id]);

  const getChartData = (data, valueKey = 'count') => {
    return data.map(item => ({
      date: moment(item.date).format('MM/DD'),
      [valueKey]: item[valueKey]
    }));
  };

  const getPlatformChartData = (platforms) => {
    return [
      { name: 'Mobile', value: platforms.mobile },
      { name: 'Desktop', value: platforms.desktop },
      { name: 'Tablet', value: platforms.tablet }
    ];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border rounded shadow-sm">
          <p className="mb-0">{`Date: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} className="mb-0" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.dataKey === 'revenue' || entry.dataKey === 'transactions' ? 
                `$${entry.value.toFixed(2)}` : entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const GrowthBadge = ({ value }) => (
    <Badge bg={value >= 0 ? 'success' : 'danger'} className="ms-2">
      {value >= 0 ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
    </Badge>
  );

  const LoadingOverlay = () => (
    <div 
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 'inherit'
      }}
    >
      <div className="text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Updating data...</div>
      </div>
    </div>
  );

  const CardWithLoading = ({ children, showLoading = true }) => (
    <div style={{ position: 'relative' }}>
      {children}
      {dataLoading && showLoading && <LoadingOverlay />}
    </div>
  );

  const DateRangePickerModal = useCallback(() => (
    <Modal 
      show={showDatePicker} 
      onHide={() => {
        setShowDatePicker(false);
        setError(null);
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Date Range</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <div>
              <DatePicker
                selected={tempStartDate}
                onChange={date => {
                  setTempStartDate(moment(date).startOf('day').toDate());
                  setError(null);
                }}
                selectsStart
                startDate={tempStartDate}
                endDate={tempEndDate}
                maxDate={tempEndDate}
                dateFormat="MMMM d, yyyy"
                className="form-control"
                placeholderText="Select start date"
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <div>
              <DatePicker
                selected={tempEndDate}
                onChange={date => {
                  setTempEndDate(moment(date).endOf('day').toDate());
                  setError(null);
                }}
                selectsEnd
                startDate={tempStartDate}
                endDate={tempEndDate}
                minDate={tempStartDate}
                maxDate={new Date()}
                dateFormat="MMMM d, yyyy"
                className="form-control"
                placeholderText="Select end date"
              />
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={() => {
            setShowDatePicker(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleApplyDateRange}
          disabled={!tempStartDate || !tempEndDate || dataLoading}
        >
          {dataLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Loading...
            </>
          ) : (
            'Apply'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  ), [showDatePicker, tempStartDate, tempEndDate, dataLoading, error, handleApplyDateRange]);

  // Add responsive chart height calculation
  const getChartHeight = useCallback(() => {
    return window.innerWidth < 768 ? 200 : 300;
  }, []);

  const [chartHeight, setChartHeight] = useState(getChartHeight());

  useEffect(() => {
    const handleResize = () => {
      setChartHeight(getChartHeight());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getChartHeight]);

  const EventOrdersList = () => {
    const [sortField, setSortField] = useState('orderCount');
    const [sortDirection, setSortDirection] = useState('desc');

    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
    };

    const sortedEvents = [...eventOrders.events].sort((a, b) => {
      const multiplier = sortDirection === 'desc' ? 1 : -1;
      if (sortField === 'orderCount') {
        return (b.orderCount - a.orderCount) * multiplier;
      } else if (sortField === 'revenue') {
        return (b.revenue - a.revenue) * multiplier;
      } else if (sortField === 'transactions') {
        return (b.transactions - a.transactions) * multiplier;
      } else if (sortField === 'lastOrderDate') {
        return (b.lastOrderDate - a.lastOrderDate) * multiplier;
      }
      return 0;
    });

    const SortIcon = ({ field }) => {
      if (sortField !== field) return <span className="ms-1 text-muted">↕</span>;
      return (
        <span className="ms-1">
          {sortDirection === 'desc' ? '↓' : '↑'}
        </span>
      );
    };

    return (
      <Card className="shadow-sm">
        <Card.Body className="p-2 p-md-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fs-6 fs-md-5">Event Orders</h5>
            <Badge bg="primary" className="fs-7">
              {eventOrders.events.length} Events
            </Badge>
          </div>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Event</th>
                  <th 
                    className="text-center" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('orderCount')}
                  >
                    Orders
                    <SortIcon field="orderCount" />
                  </th>
                  <th 
                    className="text-end" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('transactions')}
                  >
                    Transactions
                    <SortIcon field="transactions" />
                  </th>
                  <th 
                    className="text-end" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('lastOrderDate')}
                  >
                    Last Order
                    <SortIcon field="lastOrderDate" />
                  </th>
                  <th className="text-end">Created</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event, index) => (
                  <tr key={event.eventId}>
                    <td className="text-muted">{index + 1}</td>
                    <td className="text-truncate" style={{ maxWidth: '200px' }}>
                      {event.title}
                    </td>
                    <td className="text-center">
                      <Badge bg="success">{event.orderCount}</Badge>
                    </td>
                    <td className="text-end">
                      <span className={event.revenue > 0 ? 'text-success' : 'text-muted'}>
                        ${event.transactions ? event.transactions.toFixed(2) : '0.00'}
                      </span>
                    </td>
                    <td className="text-end small">
                      {event.lastOrderDate ? moment(event.lastOrderDate).format('MM/DD/YY HH:mm') : 'N/A'}
                    </td>
                    <td className="text-end small">
                      {event.timestamp ? moment(event.timestamp.toDate()).format('MM/DD/YY') : 'N/A'}
                    </td>
                  </tr>
                ))}
                {eventOrders.events.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-3">
                      No events with orders in this period
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const CreatedEventsList = () => {
    const [sortField, setSortField] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    // Default: show only events with status 'live'; option to show all
    const [statusFilter, setStatusFilter] = useState('live');

    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
    };

    const filteredEvents = statusFilter === 'live'
      ? createdEventsData.events.filter((e) => (e.status || '').toLowerCase() === 'live')
      : createdEventsData.events;

    const sortedEvents = [...filteredEvents].sort((a, b) => {
      const multiplier = sortDirection === 'desc' ? 1 : -1;
      if (sortField === 'timestamp') {
        return (b.timestamp.toDate() - a.timestamp.toDate()) * multiplier;
      } else if (sortField === 'startDate') {
        if (!a.startDate && !b.startDate) return 0;
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return (b.startDate - a.startDate) * multiplier;
      } else if (sortField === 'title') {
        return a.title.localeCompare(b.title) * multiplier;
      } else if (sortField === 'status') {
        return a.status.localeCompare(b.status) * multiplier;
      }
      return 0;
    });

    const SortIcon = ({ field }) => {
      if (sortField !== field) return <span className="ms-1 text-muted">↕</span>;
      return (
        <span className="ms-1">
          {sortDirection === 'desc' ? '↓' : '↑'}
        </span>
      );
    };

    return (
      <Card className="shadow-sm">
        <Card.Body className="p-2 p-md-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0 fs-6 fs-md-5">Created Events</h5>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select
                size="sm"
                style={{ width: 'auto', minWidth: '100px' }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="fs-7"
              >
                <option value="live">Live only</option>
                <option value="all">All</option>
              </Form.Select>
              <Badge bg="success" className="fs-7">
                {filteredEvents.length} Events
              </Badge>
              {(() => {
                const statusCounts = {};
                filteredEvents.forEach(event => {
                  statusCounts[event.status] = (statusCounts[event.status] || 0) + 1;
                });
                return Object.entries(statusCounts).map(([status, count]) => (
                  <Badge 
                    key={status}
                    bg={status === 'active' || status === 'live' ? 'success' : 
                        status === 'draft' ? 'warning' : 
                        status === 'private' ? 'dark' :
                        status === 'cancelled' ? 'danger' : 
                        status === 'published' ? 'primary' :
                        status === 'pending' ? 'info' : 'secondary'} 
                    className="fs-7"
                  >
                    {status}: {count}
                  </Badge>
                ));
              })()}
            </div>
          </div>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
                              <thead>
                  <tr>
                    <th>#</th>
                    <th>Event ID</th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('title')}
                    >
                      Event Name
                      <SortIcon field="title" />
                    </th>
                    <th 
                      className="text-center" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('status')}
                    >
                      Status
                      <SortIcon field="status" />
                    </th>
                    <th 
                      className="text-center" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('startDate')}
                    >
                      Start Date
                      <SortIcon field="startDate" />
                    </th>
                    <th className="text-center">End Date</th>
                    <th>Location</th>
                    <th>Host</th>
                    <th>Organization</th>
                    <th 
                      className="text-end" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('timestamp')}
                    >
                      Created
                      <SortIcon field="timestamp" />
                    </th>
                  </tr>
                </thead>
                              <tbody>
                  {sortedEvents.map((event, index) => (
                    <tr key={event.id}>
                      <td className="text-muted">{index + 1}</td>
                      <td>
                        <div className="small text-muted font-monospace">{event.id}</div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 fw-bold small link-primary text-decoration-none"
                          onClick={() => setEventModalEvent({ id: event.id, title: event.title })}
                        >
                          {event.title}
                        </button>
                        {event.type !== 'unknown' && (
                          <div className="text-muted small">
                            <Badge bg="info" className="fs-7">
                              {event.type}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <Badge bg={event.status === 'active' || event.status === 'live' ? 'success' : 
                                   event.status === 'draft' ? 'warning' : 
                                   event.status === 'private' ? 'dark' :
                                   event.status === 'cancelled' ? 'danger' : 
                                   event.status === 'published' ? 'primary' :
                                   event.status === 'pending' ? 'info' : 'secondary'} 
                               className="fs-7">
                          {event.status}
                        </Badge>
                      </td>
                      <td className="text-center small">
                        {event.startDate ? moment(event.startDate).format('MM/DD/YY') : 'N/A'}
                      </td>
                      <td className="text-center small">
                        {event.endDate ? moment(event.endDate).format('MM/DD/YY') : 'N/A'}
                      </td>
                      <td>
                        <div className="small text-truncate" style={{ maxWidth: '150px' }}>
                          {event.location}
                        </div>
                      </td>
                      <td>
                        {event.hostInfo ? (
                          <div>
                            <div className="fw-bold small">{event.hostInfo.userName}</div>
                            <div className="text-muted small">UID: {event.hostInfo.uid}</div>
                          </div>
                        ) : (
                          <span className="text-muted small">Unknown</span>
                        )}
                      </td>
                      <td>
                        {event.organizationInfo ? (
                          <div>
                            <div className="fw-bold small">{event.organizationInfo.organizationName}</div>
                            {event.organizationInfo.organizationId && (
                              <div className="text-muted small">ID: {event.organizationInfo.organizationId}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="text-end small">
                        {moment(event.timestamp.toDate()).format('MM/DD/YY HH:mm')}
                      </td>
                    </tr>
                  ))}
                  {filteredEvents.length === 0 && (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-3">
                        {statusFilter === 'live'
                          ? 'No live events in this period. Switch to "All" to see other statuses.'
                          : 'No events created in this period'}
                      </td>
                    </tr>
                  )}
                </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Card className="shadow-sm">
          <Card.Body>
            <div className="text-center">
              <Spinner animation="border" role="status" className="me-2" />
              <span>Loading...</span>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Card className="shadow-sm">
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>Unauthorized Access</Alert.Heading>
              <p>
                You do not have permission to view this page. Please contact your administrator if you believe this is a mistake.
              </p>
              <hr />
              <div className="d-flex justify-content-end">
                <Button variant="outline-danger" onClick={() => navigate('/')}>
                  Return to Home
                </Button>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="p-2 p-md-4" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h2 className="mb-0 fs-4 fs-md-2">Analytics Dashboard</h2>
          <small className="text-muted d-block">
            {moment(startDate).format('MMM D, YYYY')} - {moment(endDate).format('MMM D, YYYY')}
            {customDateRange && ' (Custom Range)'}
          </small>
        </div>
        <Stack direction="horizontal" gap={2} className="flex-wrap justify-content-start justify-content-md-end">
          {timeRanges.map(range => (
            <Button
              key={range.days}
              size="sm"
              variant={
                range.days === 'custom' && customDateRange
                  ? 'primary'
                  : timeRange === range.days && !customDateRange
                  ? 'primary'
                  : 'outline-primary'
              }
              onClick={() => handleTimeRangeButtonClick(range.days)}
              className="px-2 px-md-3 mb-2 mb-md-0"
              disabled={dataLoading}
            >
              {dataLoading && range.days === timeRange ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
              ) : range.days === 'custom' ? (
                <FaCalendarAlt className="me-1" />
              ) : null}
              {range.label}
            </Button>
          ))}
        </Stack>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row className="g-3">
        <Col xs={12} md={4}>
          <CardWithLoading>
            <Card 
              className="shadow-sm h-100" 
              style={{ cursor: 'pointer' }}
              onClick={handleOrdersClick}
            >
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-primary bg-opacity-10">
                    <FaShoppingCart className="text-primary" size={20} />
                  </div>
                  <div className="ms-2 ms-md-3">
                    <h6 className="mb-0">Orders</h6>
                    <h3 className="mb-0 fs-4 fs-md-3">
                      {orderData.total}
                      <GrowthBadge value={orderData.growth} />
                    </h3>
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <div className="d-flex align-items-center">
                      <FaMoneyBill className="text-success me-1 me-md-2" size={14} />
                      <span className="small">Paid: {orderData.paidOrders}</span>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex align-items-center">
                      <FaGift className="text-warning me-1 me-md-2" size={14} />
                      <span className="small">Free: {orderData.freeOrders}</span>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={getChartData(orderData.daily)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Orders"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>

        <Col xs={12} md={4}>
          <CardWithLoading>
            <Card 
              className="shadow-sm h-100" 
              style={{ cursor: 'pointer' }}
              onClick={handleCustomersClick}
            >
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-success bg-opacity-10">
                    <FaUsers className="text-success" size={20} />
                  </div>
                  <div className="ms-2 ms-md-3 flex-grow-1">
                    <h6 className="mb-0">Customers</h6>
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                      <span>
                        <strong>New:</strong> {customerData.new ?? 0}
                        <GrowthBadge value={customerData.growth} />
                      </span>
                      <span className="text-muted">|</span>
                      <span>
                        <strong>Actives:</strong> {customerData.active ?? 0}
                        <GrowthBadge value={customerData.activeGrowth} />
                      </span>
                    </div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart
                    data={(customerData.daily || []).map(item => ({
                      date: moment(item.date).format('MM/DD'),
                      newUsers: item.newUsers ?? 0,
                      activeUsers: item.activeUsers ?? 0
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="newUsers"
                      name="New users"
                      stroke="#198754"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="activeUsers"
                      name="Active users"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>

        <Col xs={12} md={4}>
          <CardWithLoading>
            <Card className="shadow-sm h-100">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-warning bg-opacity-10">
                    <FaChartLine className="text-warning" size={20} />
                  </div>
                  <div className="ms-2 ms-md-3">
                    <h6 className="mb-0">Visits</h6>
                    <h3 className="mb-0 fs-4 fs-md-3">
                      {visitData.total}
                      <GrowthBadge value={visitData.growth} />
                    </h3>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={getChartData(visitData.daily)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Visits"
                      stroke="#ffc107"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
      </Row>

      <Row className="mt-3 g-3">
        <Col xs={12} lg={8}>
          <CardWithLoading>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-md-3">
                <h5 className="mb-4 fs-6 fs-md-5">Revenue Overview</h5>
                <div className="d-flex flex-column flex-md-row justify-content-between mb-4">
                  <div className="mb-2 mb-md-0">
                    <h3 className="mb-0 fs-4 fs-md-3">${orderData.revenue.toFixed(2)}</h3>
                    <small className="text-muted">EventGo Revenue</small>
                    <GrowthBadge value={orderData.revenueGrowth} />
                  </div>
                  <div className="text-start text-md-end">
                    <h3 className="mb-0 fs-4 fs-md-3">
                      ${orderData.totalTransactions.toFixed(2)}
                      <GrowthBadge value={orderData.transactionGrowth} />
                    </h3>
                    <small className="text-muted">Total Transactions</small>
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-3 flex-wrap">
                  <div className="mb-2">
                    <h6 className="mb-0 fs-6">Average Order Value</h6>
                    <span className="text-muted small">
                      ${(orderData.totalTransactions / orderData.total || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="mb-2">
                    <h6 className="mb-0 fs-6">Platform Fee Rate</h6>
                    <span className="text-muted small">
                      {orderData.totalTransactions > 0 ? ((orderData.revenue / orderData.totalTransactions) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="mb-2">
                    <h6 className="mb-0 fs-6">Orders Without Rates</h6>
                    <span className={orderData.ordersWithoutRates > 0 ? "text-warning small fw-bold" : "text-success small"}>
                      {orderData.ordersWithoutRates || 0}
                      {orderData.total > 0 && (
                        <span className="text-muted">
                          {' '}({((orderData.ordersWithoutRates / orderData.total) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                {orderData.ordersWithoutRates > 0 && (
                  <Alert variant="warning" className="mb-0">
                    <small>
                      <strong>{orderData.ordersWithoutRates}</strong> paid order{orderData.ordersWithoutRates !== 1 ? 's' : ''} {orderData.ordersWithoutRates !== 1 ? 'are' : 'is'} missing <code>fees.rates</code> and {orderData.ordersWithoutRates !== 1 ? 'are' : 'is'} using the default 3.5% fee rate. (Free orders are not counted.)
                    </small>
                  </Alert>
                )}
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <LineChart data={orderData.daily.map(item => ({
                    date: moment(item.date).format('MM/DD'),
                    revenue: item.revenue,
                    transactions: item.transactions
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => `$${value.toFixed(2)}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      name="EventGo Revenue"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="transactions"
                      name="Transaction Volume"
                      stroke="#20c997"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>

        <Col xs={12} lg={4}>
          <CardWithLoading>
            <Card className="shadow-sm h-100">
              <Card.Body className="p-2 p-md-3">
                <h5 className="mb-4 fs-6 fs-md-5">Order Distribution</h5>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={orderData.daily.map(item => ({
                    date: moment(item.date).format('MM/DD'),
                    paidOrders: orderData.dailyPaidOrders.find(p => moment(p.date).format('MM/DD') === moment(item.date).format('MM/DD'))?.count || 0,
                    freeOrders: orderData.dailyFreeOrders.find(f => moment(f.date).format('MM/DD') === moment(item.date).format('MM/DD'))?.count || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickFormatter={(value) => value.toFixed(0)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar
                      dataKey="paidOrders"
                      name="Paid Orders"
                      stackId="a"
                      fill="rgba(75, 192, 192, 0.8)"
                    />
                    <Bar
                      dataKey="freeOrders"
                      name="Free Orders"
                      stackId="a"
                      fill="rgba(255, 159, 64, 0.8)"
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small">Paid Orders Rate:</span>
                    <strong>
                      {((orderData.paidOrders / (orderData.paidOrders + orderData.freeOrders)) * 100).toFixed(1)}%
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small">Free Orders Rate:</span>
                    <strong>
                      {((orderData.freeOrders / (orderData.paidOrders + orderData.freeOrders)) * 100).toFixed(1)}%
                    </strong>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
      </Row>

      <Row className="mt-3 g-3">
        <Col xs={12} lg={6}>
          <CardWithLoading>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-info bg-opacity-10 mb-2 mb-md-0">
                    <FaChartLine className="text-info" size={20} />
                  </div>
                  <div className="ms-0 ms-md-3">
                    <h5 className="mb-0 fs-6 fs-md-5">Events Overview</h5>
                    <div className="d-flex align-items-center">
                      <h3 className="mb-0 fs-4 fs-md-3">{eventData.total}</h3>
                      <GrowthBadge value={eventData.growth} />
                    </div>
                  </div>
                </div>
                <Row>
                  <Col xs={12} md={8} className="mb-3 mb-md-0">
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <LineChart data={getChartData(eventData.daily)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis 
                          tick={{ fontSize: 12 }} 
                          tickFormatter={(value) => value.toFixed(0)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                          type="monotone"
                          dataKey="count"
                          name="Events"
                          stroke="#0dcaf0"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Col>
                  <Col xs={12} md={4}>
                    <h6 className="mb-3 fs-6">Events by Type</h6>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={Object.entries(eventData.byType).map(([type, value]) => ({
                            name: type,
                            value
                          }))}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#0dcaf0"
                          label={{ fontSize: 12 }}
                        >
                          {Object.entries(eventData.byType).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={[
                                '#0dcaf0',
                                '#20c997',
                                '#6610f2',
                                '#fd7e14',
                                '#198754'
                              ][index % 5]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
        <Col xs={12} lg={6}>
          <CardWithLoading>
            <EventOrdersList />
          </CardWithLoading>
        </Col>
      </Row>

      <Row className="mt-3 g-3">
        <Col xs={12}>
          <CardWithLoading>
            <CreatedEventsList />
          </CardWithLoading>
        </Col>
      </Row>

      <Row className="mt-3 g-3">
        <Col xs={12} lg={6}>
          <CardWithLoading>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
                  <div className="rounded-circle p-2 p-md-3" style={{ backgroundColor: 'rgba(111, 66, 193, 0.1)' }}>
                    <FaUsers style={{ color: '#6f42c1' }} size={20} />
                  </div>
                  <div className="ms-0 ms-md-3">
                    <h5 className="mb-0 fs-6 fs-md-5">Organizations Overview</h5>
                    <div className="d-flex align-items-center">
                      <h3 className="mb-0 fs-4 fs-md-3">{organizationData.total}</h3>
                      <GrowthBadge value={organizationData.growth} />
                    </div>
                  </div>
                </div>
                <Row>
                  <Col xs={12} md={8} className="mb-3 mb-md-0">
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <LineChart data={getChartData(organizationData.daily)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis 
                          tick={{ fontSize: 12 }} 
                          tickFormatter={(value) => value.toFixed(0)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                          type="monotone"
                          dataKey="count"
                          name="Organizations"
                          stroke="#6f42c1"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Col>
                  <Col xs={12} md={4}>
                    <h6 className="mb-3 fs-6">Organizations by Type</h6>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={Object.entries(organizationData.byType).map(([type, value]) => ({
                            name: type,
                            value
                          }))}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#6f42c1"
                          label={{ fontSize: 12 }}
                        >
                          {Object.entries(organizationData.byType).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={[
                                '#6f42c1',
                                '#e83e8c',
                                '#fd7e14',
                                '#20c997',
                                '#0dcaf0'
                              ][index % 5]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
        <Col xs={12} lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fs-6 fs-md-5">Organizations List</h5>
                <Badge bg="primary" className="fs-7">
                  {organizationData.total} Organizations
                </Badge>
              </div>
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Organization</th>
                      <th className="text-center">Members</th>
                      <th className="text-center">Events</th>
                      <th className="text-end">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizationData.organizations && organizationData.organizations.length > 0 ? (
                      organizationData.organizations.map((org, index) => (
                        <tr key={org.id}>
                          <td className="text-muted">{index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="me-3">
                                <img 
                                  src={org.profileURL || 'https://via.placeholder.com/40x40?text=O'} 
                                  alt={org.name || 'Organization'} 
                                  className="rounded-circle"
                                  style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/40x40?text=O';
                                  }}
                                />
                              </div>
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-bold text-truncate">{org.name || 'Untitled Organization'}</div>
                                <small className="text-muted">{org.type || 'Unknown Type'}</small>
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            <Badge bg="info">{org.memberCount || 0}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="success">{org.eventCount || 0}</Badge>
                          </td>
                          <td className="text-end small">
                            {org.timestamp ? moment(org.timestamp.toDate()).format('MM/DD/YY') : 'N/A'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          {dataLoading ? 'Loading organizations...' : 'No organizations in this period'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3 g-3">
        <Col xs={12} lg={6}>
          <CardWithLoading>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-secondary bg-opacity-10">
                    <FaChartLine className="text-secondary" size={20} />
                  </div>
                  <div className="ms-0 ms-md-3">
                    <h5 className="mb-0 fs-6 fs-md-5">Page Views</h5>
                    <div className="d-flex align-items-center">
                      <h3 className="mb-0 fs-4 fs-md-3">{visitData.total}</h3>
                    </div>
                  </div>
                </div>
                <Row>
                  <Col xs={12} md={8} className="mb-3 mb-md-0">
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <LineChart data={getChartData(visitData.daily)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis 
                          tick={{ fontSize: 12 }} 
                          tickFormatter={(value) => value.toFixed(0)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                          type="monotone"
                          dataKey="count"
                          name="Page Views"
                          stroke="#6c757d"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Col>
                  <Col xs={12} md={4}>
                    <h6 className="mb-3 fs-6">User Agent Breakdown</h6>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small">With User Agent:</span>
                        <strong>{visitData.withUserAgent}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="small">Without User Agent:</span>
                        <strong>{visitData.withoutUserAgent}</strong>
                      </div>
                    </div>
                    {Object.keys(visitData.userAgents).length > 0 && (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={Object.entries(visitData.userAgents).map(([type, value]) => ({
                              name: type,
                              value
                            }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#6c757d"
                            label={{ fontSize: 12 }}
                          >
                            {Object.entries(visitData.userAgents).map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={[
                                  '#6c757d',
                                  '#20c997',
                                  '#6610f2',
                                  '#fd7e14',
                                  '#198754',
                                  '#0dcaf0',
                                  '#e83e8c'
                                ][index % 7]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
        <Col xs={12} lg={6}>
          <CardWithLoading>
            <Card className="shadow-sm h-100">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fs-6 fs-md-5">User Agent Details</h5>
                  <Badge bg="secondary" className="fs-7">
                    {visitData.withUserAgent} with UA
                  </Badge>
                </div>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>User Agent Type</th>
                        <th className="text-center">Count</th>
                        <th className="text-end">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(visitData.userAgents).length > 0 ? (
                        Object.entries(visitData.userAgents)
                          .sort(([,a], [,b]) => b - a)
                          .map(([type, count], index) => (
                            <tr key={type}>
                              <td className="text-muted">{index + 1}</td>
                              <td>{type}</td>
                              <td className="text-center">
                                <Badge bg="secondary">{count}</Badge>
                              </td>
                              <td className="text-end">
                                {visitData.withUserAgent > 0 ? 
                                  ((count / visitData.withUserAgent) * 100).toFixed(1) : 0}%
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-3">
                            No user agent data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
      </Row>

      <Row className="mt-3 g-3">
        <Col xs={12} lg={6}>
          <CardWithLoading>
            <Card className="shadow-sm">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
                  <div className="rounded-circle p-2 p-md-3 bg-info bg-opacity-10">
                    <FaChartLine className="text-info" size={20} />
                  </div>
                  <div className="ms-0 ms-md-3">
                    <h5 className="mb-0 fs-6 fs-md-5">URL Analysis</h5>
                    <div className="d-flex align-items-center">
                      <h3 className="mb-0 fs-4 fs-md-3">{visitData.urlBreakdown.length}</h3>
                    </div>
                  </div>
                </div>
                <Row>
                  <Col xs={12} md={6} className="mb-3 mb-md-0">
                    <h6 className="mb-3 fs-6">Domain Distribution</h6>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={Object.entries(visitData.domains).map(([domain, value]) => ({
                            name: domain,
                            value
                          }))}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#0dcaf0"
                          label={{ fontSize: 12 }}
                        >
                          {Object.entries(visitData.domains).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={[
                                '#0dcaf0',
                                '#20c997',
                                '#6c757d'
                              ][index % 3]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Col>
                  <Col xs={12} md={6}>
                    <h6 className="mb-3 fs-6">Page Type Distribution</h6>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={Object.entries(visitData.pageTypes).map(([type, value]) => ({
                            name: type,
                            value
                          }))}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#6610f2"
                          label={{ fontSize: 12 }}
                        >
                          {Object.entries(visitData.pageTypes).map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={[
                                '#6610f2',
                                '#fd7e14',
                                '#6c757d'
                              ][index % 3]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
        <Col xs={12} lg={6}>
          <CardWithLoading>
            <Card className="shadow-sm h-100">
              <Card.Body className="p-2 p-md-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fs-6 fs-md-5">URL Breakdown</h5>
                  <Badge bg="info" className="fs-7">
                    {visitData.urlBreakdown.length} URLs
                  </Badge>
                </div>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Domain</th>
                        <th>Page Type</th>
                        <th>Event Name</th>
                        <th>User</th>
                        <th className="text-center">Platform</th>
                        <th className="text-end">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitData.urlBreakdown.length > 0 ? (
                        visitData.urlBreakdown
                          .slice((urlPage - 1) * 10, urlPage * 10)
                          .map((url, index) => (
                            <tr key={(urlPage - 1) * 10 + index}>
                              <td className="text-muted">{(urlPage - 1) * 10 + index + 1}</td>
                              <td>
                                <Badge 
                                  bg={url.domain === 'evtgo.com' ? 'primary' : 
                                      url.domain === 'eventgo.ca' ? 'success' : 'secondary'}
                                  className="fs-7"
                                >
                                  {url.domain}
                                </Badge>
                              </td>
                              <td>
                                <Badge 
                                  bg={url.pageType === 'event page' ? 'info' : 
                                      url.pageType === 'host page' ? 'warning' :
                                      url.pageType === 'hosts page' ? 'warning' :
                                      url.pageType === 'search event page' ? 'success' :
                                      url.pageType === 'ask-ai page' ? 'danger' :
                                      url.pageType === 'create event page' ? 'primary' :
                                      url.pageType === 'order page' ? 'dark' :
                                      url.pageType === 'about page' ? 'secondary' : 'light'}
                                  className="fs-7"
                                >
                                  {url.pageType}
                                </Badge>
                              </td>
                              <td>
                                {url.eventDetails ? (
                                  <div>
                                    <div className="fw-bold small">
                                      {url.eventDetails.title}
                                    </div>
                                    <div className="text-muted small">
                                      ID: {url.eventDetails.id}
                                    </div>
                                  </div>
                                ) : url.eventId ? (
                                  <Badge bg="secondary" className="fs-7">
                                    {url.eventId}
                                  </Badge>
                                ) : (
                                  <span className="text-muted small">-</span>
                                )}
                              </td>
                              <td>
                                {url.userDetails ? (
                                  <div>
                                    <div className="fw-bold small">
                                      {url.userDetails.firstName} {url.userDetails.lastName}
                                    </div>
                                    <div className="text-muted small">
                                      {url.userDetails.email}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted small">Anonymous</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Badge bg="light" text="dark" className="fs-7">
                                  {url.platform || 'unknown'}
                                </Badge>
                              </td>
                              <td className="text-end small">
                                {moment(url.timestamp.toDate()).format('MM/DD HH:mm')}
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-3">
                            No URL data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                {visitData.urlBreakdown.length > 10 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      Showing {((urlPage - 1) * 10) + 1} - {Math.min(urlPage * 10, visitData.urlBreakdown.length)} of {visitData.urlBreakdown.length} URLs
                    </small>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={urlPage === 1}
                        onClick={() => setUrlPage(urlPage - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={urlPage >= Math.ceil(visitData.urlBreakdown.length / 10)}
                        onClick={() => setUrlPage(urlPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </CardWithLoading>
        </Col>
      </Row>

      <DateRangePickerModal />

      <Modal
        show={!!eventModalEvent}
        onHide={() => setEventModalEvent(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{eventModalEvent?.title || 'Event summary'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {eventModalLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading event summary...
            </div>
          ) : eventModalData ? (
            <>
              <Row className="g-2 mb-3">
                <Col xs={6} md={3}>
                  <Card className="border-primary h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">User count</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.userCount}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={3}>
                  <Card className="border-success h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Order count</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.orderCount}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={3}>
                  <Card className="border-info h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Order total</Card.Text>
                      <Card.Title className="mb-0 fs-6">${(eventModalData.orderTotal || 0).toFixed(2)}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={3}>
                  <Card className="border-warning h-100">
                    <Card.Body className="py-2 px-3 text-center">
                      <Card.Text className="small text-muted mb-0">Views count</Card.Text>
                      <Card.Title className="mb-0 fs-6">{eventModalData.viewsCount}</Card.Title>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Card className="mb-0">
                <Card.Body>
                  <Card.Title className="fs-6 mb-2">Users, orders & views over time (by day)</Card.Title>
                  {eventModalData.daily?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart
                        data={eventModalData.daily.map(row => ({
                          ...row,
                          dateLabel: row.date ? `${row.date.slice(5, 7)}/${row.date.slice(8, 10)}` : ''
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => Number(v).toFixed(0)} />
                        <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''} />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line type="monotone" dataKey="users" name="Users" stroke="#0d6efd" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="orders" name="Orders" stroke="#198754" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="views" name="Views" stroke="#fd7e14" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted small mb-0">No daily data for this event.</p>
                  )}
                </Card.Body>
              </Card>
            </>
          ) : (
            <p className="text-muted mb-0">No data.</p>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
} 